import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  MapPin,
  Search,
  Target,
  DollarSign,
  Eye,
  Star,
  MousePointerClick,
  Globe,
  Loader2,
  RefreshCw,
  Plus,
  Trash2,
  AlertTriangle,
  CheckCircle,
  Info,
  Shield
} from 'lucide-react';
import ClientDetailSEOSection from '../components/ClientDetailSEOSection';
import BillingSection from '../components/BillingSection';
import api, {
  Client,
  Market,
  ClientFinding,
  ClientMarketingSummary,
  ClientOnboarding,
  GoogleAdsDashboard,
  GBPDashboard,
  GSCDashboard
} from '../services/api';

type TabType = 'overview' | 'google-ads' | 'gbp' | 'seo' | 'seo-audit' | 'markets';

const ClientDetail = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [client, setClient] = useState<Client | null>(null);
  const [marketingSummary, setMarketingSummary] = useState<ClientMarketingSummary | null>(null);
  const [googleAdsDashboard, setGoogleAdsDashboard] = useState<GoogleAdsDashboard | null>(null);
  const [gbpDashboard, setGbpDashboard] = useState<GBPDashboard | null>(null);
  const [gscDashboard, setGscDashboard] = useState<GSCDashboard | null>(null);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [checkResults, setCheckResults] = useState<{ findings: ClientFinding[], sop_packs: string[] } | null>(null);
  const [showMarketForm, setShowMarketForm] = useState(false);
  const [marketForm, setMarketForm] = useState({ city: '', region: '', zip_codes: '', city_page_url: '', is_primary: false });
  const [loading, setLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);
  const [onboardingData, setOnboardingData] = useState<ClientOnboarding | null>(null);

  const loadClientData = useCallback(async () => {
    if (!clientId) return;
    try {
      setLoading(true);
      const [clientData, summaryData] = await Promise.all([
        api.getClient(clientId),
        api.getClientMarketingSummary(clientId)
      ]);
      setClient(clientData);
      setMarketingSummary(summaryData);
      // Load onboarding status if client is in initial phase
      if (clientData.phase === 'initial') {
        try {
          const obData = await api.getClientOnboarding(clientId);
          setOnboardingData(obData);
        } catch { /* onboarding not started yet is fine */ }
      }
    } catch (error) {
      console.error('Failed to load client:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadClientData();
  }, [loadClientData]);

  useEffect(() => {
    if (!clientId) return;

    const loadTabData = async () => {
      setTabLoading(true);
      try {
        if (activeTab === 'google-ads' && !googleAdsDashboard) {
          const data = await api.getGoogleAdsDashboard(clientId);
          setGoogleAdsDashboard(data);
        } else if (activeTab === 'gbp' && !gbpDashboard) {
          const data = await api.getGBPDashboard(clientId);
          setGbpDashboard(data);
        } else if (activeTab === 'seo' && !gscDashboard) {
          const data = await api.getGSCDashboard(clientId);
          setGscDashboard(data);
        }
      } catch (error) {
        console.error('Failed to load tab data:', error);
      } finally {
        setTabLoading(false);
      }
    };

    if (activeTab !== 'overview' && activeTab !== 'markets') {
      loadTabData();
    }
    if (activeTab === 'markets') {
      loadMarkets();
    }
  }, [activeTab, clientId, googleAdsDashboard, gbpDashboard, gscDashboard]);

  const formatCurrency = (value: number | string) => {
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `$${(num || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatNumber = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return '0';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return (num || 0).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <div className="text-center">
          <p className="text-xs text-gray-600 dark:text-gray-300">Client not found</p>
          <Link to="/clients" className="text-xs text-primary-500 hover:underline mt-1 inline-block">
            Back to Clients
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: BarChart3 },
    { id: 'google-ads' as TabType, label: 'Google Ads', icon: Target },
    { id: 'gbp' as TabType, label: 'GBP', icon: MapPin },
    { id: 'seo' as TabType, label: 'SEO', icon: Search },
    { id: 'seo-audit' as TabType, label: 'SEO Audit', icon: Shield },
    { id: 'markets' as TabType, label: 'Markets', icon: Globe },
  ];

  const loadMarkets = async () => {
    if (!clientId) return;
    try {
      const data = await api.getClientMarkets(clientId);
      setMarkets(data);
    } catch (e) { console.error('Failed to load markets:', e); }
  };

  const handleAddMarket = async () => {
    if (!clientId || !marketForm.city || !marketForm.region) return;
    try {
      await api.createMarket(clientId, {
        city: marketForm.city,
        region: marketForm.region,
        zip_codes: marketForm.zip_codes ? marketForm.zip_codes.split(',').map((z: string) => z.trim()) : [],
        city_page_url: marketForm.city_page_url,
        is_primary: marketForm.is_primary,
      });
      setMarketForm({ city: '', region: '', zip_codes: '', city_page_url: '', is_primary: false });
      setShowMarketForm(false);
      loadMarkets();
    } catch (e) { console.error('Failed to add market:', e); }
  };

  const handleDeleteMarket = async (marketId: string) => {
    if (!clientId) return;
    try {
      await api.deleteMarket(clientId, marketId);
      loadMarkets();
    } catch (e) { console.error('Failed to delete market:', e); }
  };

  const handleRunChecks = async () => {
    if (!clientId) return;
    try {
      const results = await api.runClientChecks(clientId);
      setCheckResults({ findings: results.findings, sop_packs: results.sop_packs });
    } catch (e) { console.error('Failed to run checks:', e); }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header - Compact */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex items-center gap-3">
            <Link to="/clients" className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors">
              <ArrowLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
            </Link>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{client.company}</h1>
                <span className={`status-badge ${
                  client.status === 'active' ? 'status-active' :
                  client.status === 'internal' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' :
                  'status-paused'
                }`}>
                  {client.status}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{client.name} - {client.industry_display}</p>
              {client.google_exposure_model && (
                <div className="flex gap-1 mt-0.5">
                  <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded font-medium">
                    {client.google_exposure_model.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  {client.scale_pattern && (
                    <span className="text-[10px] px-1.5 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded font-medium">
                      {client.scale_pattern.replace(/_/g, ' ')}
                    </span>
                  )}
                  {client.demand_modes?.map((m: string) => (
                    <span key={m} className="text-[10px] px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded">{m}</span>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={loadClientData}
              disabled={loading}
              className="btn btn-secondary"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 py-3">
        {/* Tabs - Compact */}
        <div className="flex gap-1 mb-3 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-black'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-light-border dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tabLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
          </div>
        ) : (
          <>
            {activeTab === 'overview' && marketingSummary && (
              <div className="space-y-2">
                {/* Onboarding Progress */}
                {client.phase === 'initial' && onboardingData && (
                  <Link to={`/clients/${clientId}/onboarding`} className="block panel p-3 hover:ring-2 hover:ring-[var(--color-primary)] transition-all">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[var(--color-primary)]" />
                        <h3 className="text-xs font-semibold">Onboarding Progress</h3>
                      </div>
                      <span className="text-xs font-medium text-[var(--color-primary)]">
                        {onboardingData.progress_percentage}% — Step {onboardingData.current_step}/7
                      </span>
                    </div>
                    <div className="w-full bg-[var(--bg-secondary)] rounded-full h-1.5">
                      <div
                        className="bg-[var(--color-primary)] h-1.5 rounded-full transition-all"
                        style={{ width: `${onboardingData.progress_percentage}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-[var(--text-secondary)] mt-1.5">Continue Setup →</p>
                  </Link>
                )}
                {client.phase === 'initial' && !onboardingData && (
                  <Link to={`/clients/${clientId}/onboarding`} className="block panel p-3 hover:ring-2 hover:ring-[var(--color-primary)] transition-all">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[var(--text-secondary)]" />
                      <h3 className="text-xs font-semibold">Start Onboarding</h3>
                    </div>
                    <p className="text-[10px] text-[var(--text-secondary)] mt-1">Begin the 7-step setup process →</p>
                  </Link>
                )}

                {/* Billing */}
                {client.billing_enabled && (
                  <BillingSection clientId={clientId!} />
                )}

                {/* Findings Banner */}
                {(client.findings && client.findings.length > 0) && (
                  <div className="panel p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Classification Findings</h3>
                      </div>
                      <button onClick={handleRunChecks} className="text-xs text-primary-500 hover:text-primary-600 font-medium">Re-check</button>
                    </div>
                    <div className="space-y-1">
                      {client.findings.map((f: ClientFinding) => (
                        <div key={f.id} className={`flex items-start gap-2 text-xs px-2 py-1 rounded ${
                          f.severity === 'critical' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' :
                          f.severity === 'warn' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300' :
                          'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                        }`}>
                          {f.severity === 'critical' ? <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> :
                           f.severity === 'warn' ? <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> :
                           <Info className="w-3 h-3 mt-0.5 shrink-0" />}
                          <span>{f.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* SOP Packs */}
                {(client.sop_packs && client.sop_packs.length > 0) && (
                  <div className="panel p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-4 h-4 text-green-500" />
                      <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Active SOP Packs</h3>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {client.sop_packs.map((pack: string) => (
                        <span key={pack} className="text-[10px] px-2 py-1 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 rounded font-medium border border-green-200 dark:border-green-800">
                          {pack.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {/* Google Ads Card */}
                <div className="panel p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Target className="w-4 h-4 text-blue-500" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Google Ads</h3>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Accounts</span>
                      <span className="font-medium text-gray-900 dark:text-white">{marketingSummary.google_ads.accounts_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Spend</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatCurrency(marketingSummary.google_ads.total_spend)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Clicks</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(marketingSummary.google_ads.total_clicks)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Conversions</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(marketingSummary.google_ads.total_conversions)}</span>
                    </div>
                  </div>
                  <button onClick={() => setActiveTab('google-ads')} className="mt-2 w-full text-xs text-primary-500 hover:text-primary-600 font-medium">
                    View Details
                  </button>
                </div>

                {/* GBP Card */}
                <div className="panel p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-green-500" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Google Business</h3>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Locations</span>
                      <span className="font-medium text-gray-900 dark:text-white">{marketingSummary.gbp.locations_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Reviews</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(marketingSummary.gbp.total_reviews)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Avg Rating</span>
                      <span className="font-medium text-gray-900 dark:text-white flex items-center gap-1">
                        <Star className="w-3 h-3 text-yellow-500" />
                        {marketingSummary.gbp.avg_rating.toFixed(1)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Views</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(marketingSummary.gbp.total_views)}</span>
                    </div>
                  </div>
                  <button onClick={() => setActiveTab('gbp')} className="mt-2 w-full text-xs text-primary-500 hover:text-primary-600 font-medium">
                    View Details
                  </button>
                </div>

                {/* SEO Card */}
                <div className="panel p-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Globe className="w-4 h-4 text-purple-500" />
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white">SEO</h3>
                  </div>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Properties</span>
                      <span className="font-medium text-gray-900 dark:text-white">{marketingSummary.gsc.properties_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Clicks</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(marketingSummary.gsc.total_clicks)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Impressions</span>
                      <span className="font-medium text-gray-900 dark:text-white">{formatNumber(marketingSummary.gsc.total_impressions)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Avg Position</span>
                      <span className="font-medium text-gray-900 dark:text-white">{marketingSummary.gsc.avg_position.toFixed(1)}</span>
                    </div>
                  </div>
                  <button onClick={() => setActiveTab('seo')} className="mt-2 w-full text-xs text-primary-500 hover:text-primary-600 font-medium">
                    View Details
                  </button>
                </div>
              </div>
              </div>
            )}

            {activeTab === 'markets' && (
              <div className="space-y-2">
                <div className="panel p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      <h3 className="text-xs font-semibold text-gray-900 dark:text-white">Active Markets ({markets.length})</h3>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={handleRunChecks} className="btn btn-secondary text-xs">
                        <CheckCircle className="w-3 h-3" /> Run Checks
                      </button>
                      <button onClick={() => { setShowMarketForm(true); loadMarkets(); }} className="btn btn-primary text-xs">
                        <Plus className="w-3 h-3" /> Add Market
                      </button>
                    </div>
                  </div>

                  {showMarketForm && (
                    <div className="border border-gray-200 dark:border-gray-700 rounded p-3 mb-3 space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <input className="input text-xs" placeholder="Market Name * (e.g., St. Johns County)" value={marketForm.city} onChange={(e) => setMarketForm({ ...marketForm, city: e.target.value })} />
                        <input className="input text-xs" placeholder="State * (e.g., FL)" value={marketForm.region} onChange={(e) => setMarketForm({ ...marketForm, region: e.target.value })} />
                      </div>
                      <div>
                        <input className="input text-xs w-full" placeholder="ZIP codes for Google Ads targeting (comma separated)" value={marketForm.zip_codes} onChange={(e) => setMarketForm({ ...marketForm, zip_codes: e.target.value })} />
                        <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Used for precise Google Ads location targeting</p>
                      </div>
                      <div>
                        <input className="input text-xs w-full" placeholder="City page URL (optional)" value={marketForm.city_page_url} onChange={(e) => setMarketForm({ ...marketForm, city_page_url: e.target.value })} />
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-1 text-xs text-gray-700 dark:text-gray-300 cursor-pointer">
                          <input type="checkbox" checked={marketForm.is_primary} onChange={(e) => setMarketForm({ ...marketForm, is_primary: e.target.checked })} className="rounded border-gray-300" />
                          Primary market
                        </label>
                        <div className="flex gap-2">
                          <button onClick={() => setShowMarketForm(false)} className="btn btn-secondary text-xs">Cancel</button>
                          <button onClick={handleAddMarket} className="btn btn-primary text-xs">Add</button>
                        </div>
                      </div>
                    </div>
                  )}

                  {markets.length === 0 ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">No markets defined. Click "Add Market" to get started.</p>
                  ) : (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                          <th className="pb-1 font-medium">Market</th>
                          <th className="pb-1 font-medium">State</th>
                          <th className="pb-1 font-medium">ZIP Codes</th>
                          <th className="pb-1 font-medium">Landing Page</th>
                          <th className="pb-1 font-medium">GBP</th>
                          <th className="pb-1 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {markets.map((market) => (
                          <tr key={market.id} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-1.5 text-gray-900 dark:text-white">
                              {market.city}
                              {market.is_primary && <span className="ml-1 text-[10px] px-1 py-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 rounded">HQ</span>}
                            </td>
                            <td className="py-1.5 text-gray-600 dark:text-gray-400">{market.region}</td>
                            <td className="py-1.5 text-gray-600 dark:text-gray-400">{market.zip_codes?.join(', ') || '-'}</td>
                            <td className="py-1.5">{market.city_page_url ? <a href={market.city_page_url} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline truncate block max-w-[150px]">{market.city_page_url.replace(/https?:\/\//, '')}</a> : <span className="text-gray-400">-</span>}</td>
                            <td className="py-1.5 text-gray-600 dark:text-gray-400">{market.gbp_locations_count}</td>
                            <td className="py-1.5 text-right">
                              <button onClick={() => handleDeleteMarket(market.id)} className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"><Trash2 className="w-3 h-3 text-red-500" /></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Check Results */}
                {checkResults && checkResults.findings.length > 0 && (
                  <div className="panel p-3">
                    <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-2">Check Results</h3>
                    <div className="space-y-1">
                      {checkResults.findings.map((f, i) => (
                        <div key={i} className={`text-xs px-2 py-1 rounded flex items-start gap-2 ${
                          f.severity === 'critical' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' :
                          f.severity === 'warn' ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300' :
                          'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                        }`}>
                          <span className="font-medium uppercase shrink-0">{f.severity}</span>
                          <span>{f.message}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}


            {activeTab === 'google-ads' && googleAdsDashboard && (
              <div className="space-y-2">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-green-500" />
                      <span className="stat-label">Total Spend</span>
                    </div>
                    <p className="stat-value">{formatCurrency(googleAdsDashboard.accounts.total_spend)}</p>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <Eye className="w-4 h-4 text-blue-500" />
                      <span className="stat-label">Impressions</span>
                    </div>
                    <p className="stat-value">{formatNumber(googleAdsDashboard.accounts.total_impressions)}</p>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <MousePointerClick className="w-4 h-4 text-purple-500" />
                      <span className="stat-label">Clicks</span>
                    </div>
                    <p className="stat-value">{formatNumber(googleAdsDashboard.accounts.total_clicks)}</p>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <Target className="w-4 h-4 text-orange-500" />
                      <span className="stat-label">Conversions</span>
                    </div>
                    <p className="stat-value">{formatNumber(googleAdsDashboard.accounts.total_conversions)}</p>
                  </div>
                </div>

                {/* Campaigns */}
                {googleAdsDashboard.campaigns && (
                  <div className="panel p-3">
                    <h3 className="section-title mb-2">Campaign Stats</h3>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="text-center">
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{googleAdsDashboard.campaigns.total}</p>
                        <p className="text-xs text-gray-500">Total</p>
                      </div>
                      <div className="text-center">
                        <p className="text-base font-semibold text-green-600">{googleAdsDashboard.campaigns.enabled}</p>
                        <p className="text-xs text-gray-500">Enabled</p>
                      </div>
                      <div className="text-center">
                        <p className="text-base font-semibold text-yellow-600">{googleAdsDashboard.campaigns.paused}</p>
                        <p className="text-xs text-gray-500">Paused</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Accounts List */}
                {googleAdsDashboard.accounts_list && googleAdsDashboard.accounts_list.length > 0 && (
                  <div className="panel p-3">
                    <h3 className="section-title mb-2">Connected Accounts</h3>
                    <div className="space-y-1.5">
                      {googleAdsDashboard.accounts_list.map((account) => (
                        <div key={account.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{account.name}</p>
                            <p className="text-xs text-gray-500">ID: {account.customer_id}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900 dark:text-white">{formatCurrency(account.total_spend)}</p>
                            <p className="text-xs text-gray-500">{account.campaigns_count} campaigns</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'gbp' && gbpDashboard && (
              <div className="space-y-2">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <MapPin className="w-4 h-4 text-green-500" />
                      <span className="stat-label">Locations</span>
                    </div>
                    <p className="stat-value">{gbpDashboard.locations?.total || 0}</p>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <Star className="w-4 h-4 text-yellow-500" />
                      <span className="stat-label">Avg Rating</span>
                    </div>
                    <p className="stat-value">{gbpDashboard.locations?.average_rating || 0}</p>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <Eye className="w-4 h-4 text-blue-500" />
                      <span className="stat-label">Total Views</span>
                    </div>
                    <p className="stat-value">{formatNumber(gbpDashboard.locations?.total_views)}</p>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <Globe className="w-4 h-4 text-purple-500" />
                      <span className="stat-label">Website Clicks</span>
                    </div>
                    <p className="stat-value">{formatNumber(gbpDashboard.locations?.total_website_clicks)}</p>
                  </div>
                </div>

                {/* Reviews */}
                {gbpDashboard.reviews && (
                  <div className="panel p-3">
                    <h3 className="section-title mb-2">Review Stats</h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-center">
                        <p className="text-base font-semibold text-gray-900 dark:text-white">{gbpDashboard.reviews.total}</p>
                        <p className="text-xs text-gray-500">Total Reviews</p>
                      </div>
                      <div className="text-center">
                        <p className="text-base font-semibold text-orange-600">{gbpDashboard.reviews.needs_reply}</p>
                        <p className="text-xs text-gray-500">Needs Reply</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Locations List */}
                {gbpDashboard.top_locations && gbpDashboard.top_locations.length > 0 && (
                  <div className="panel p-3">
                    <h3 className="section-title mb-2">Locations</h3>
                    <div className="space-y-1.5">
                      {gbpDashboard.top_locations.map((location) => (
                        <div key={location.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 dark:text-white truncate">{location.title}</p>
                            <p className="text-xs text-gray-500 truncate">{location.full_address}</p>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className="font-medium text-gray-900 dark:text-white flex items-center justify-end gap-1">
                              <Star className="w-3 h-3 text-yellow-500" />
                              {parseFloat(String(location.average_rating || '0')).toFixed(1)}
                            </p>
                            <p className="text-xs text-gray-500">{location.total_reviews} reviews</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'seo' && gscDashboard && (
              <div className="space-y-2">
                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <Globe className="w-4 h-4 text-purple-500" />
                      <span className="stat-label">Properties</span>
                    </div>
                    <p className="stat-value">{gscDashboard.total_properties}</p>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <MousePointerClick className="w-4 h-4 text-blue-500" />
                      <span className="stat-label">Clicks</span>
                    </div>
                    <p className="stat-value">{formatNumber(gscDashboard.stats?.total_clicks)}</p>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <Eye className="w-4 h-4 text-green-500" />
                      <span className="stat-label">Impressions</span>
                    </div>
                    <p className="stat-value">{formatNumber(gscDashboard.stats?.total_impressions)}</p>
                  </div>
                  <div className="stat-card">
                    <div className="flex items-center gap-2 mb-1">
                      <BarChart3 className="w-4 h-4 text-orange-500" />
                      <span className="stat-label">Avg Position</span>
                    </div>
                    <p className="stat-value">{(gscDashboard.stats?.average_position || 0).toFixed(1)}</p>
                  </div>
                </div>

                {/* Properties List */}
                {gscDashboard.properties && gscDashboard.properties.length > 0 && (
                  <div className="panel p-3">
                    <h3 className="section-title mb-2">Search Properties</h3>
                    <div className="space-y-1.5">
                      {gscDashboard.properties.map((property) => (
                        <div key={property.id} className="flex items-center justify-between p-2 bg-gray-50 dark:bg-gray-800 rounded text-xs">
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-gray-900 dark:text-white truncate">{property.display_name || property.site_url}</p>
                            <p className="text-xs text-gray-500 truncate">{property.site_url}</p>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className="font-medium text-gray-900 dark:text-white">{formatNumber(property.total_clicks)} clicks</p>
                            <p className="text-xs text-gray-500">{formatNumber(property.total_impressions)} impr</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'seo-audit' && clientId && (
              <ClientDetailSEOSection clientId={clientId} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ClientDetail;
